import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getAllBranch, getPurchaseOrderWeeklySummary } from "../../services";
import { Branch } from "../../types/user.type";
import {
  PURCHASE_ORDER_NO_ROUTE_FILTER,
  PURCHASE_ORDER_STATUS_LABEL,
  PurchaseOrderListFilter,
  PurchaseOrderWeeklySummaryData,
} from "../../types/purchase-order.type";
import { numberWithCommas } from "../../utils";
import { formatThaiShortDate } from "../../utils/date";
import PurchaseOrderSkeleton from "./Skeleton";
import WeeklySliceDrawer, { WeeklySlice } from "./WeeklySliceDrawer";
import { PO_API_DATE_FORMAT, PO_EMPTY_TEXT } from "./data";
import {
  currentWeekStart,
  heatRatio,
  maxCellPoCount,
  orderWeeklyRoutes,
  snapToWeekStart,
  visibleDayIndexes,
  WEEK_LENGTH,
  WEEKDAY_SHORT_LABELS,
} from "./weekly";

/** แถวของใบที่ยังไม่จับคู่ร้าน (route_number null) — ยังไม่รู้สายเพราะยังไม่รู้ร้าน */
const NO_ROUTE_LABEL = PURCHASE_ORDER_STATUS_LABEL.UNMATCHED_CUSTOMER;

/** ความเข้มสูงสุดของสีพื้นช่องที่ PO เยอะที่สุด — อ่อนพอให้ตัวหนังสือยังอ่านง่าย */
const MAX_HEAT_ALPHA = 0.28;

const DEFAULT_DAY_INDEXES = [0, 1, 2, 3, 4, 5];
const SKELETON_ROWS = 6;

/** ค่าว่างของ dropdown สาขา (HO) — ยังไม่เลือก จึงยังไม่อ่านตาราง */
const NO_BRANCH_SELECTED = "";

const routeLabel = (routeNumber: string | null): string =>
  routeNumber === null ? NO_ROUTE_LABEL : `สาย ${routeNumber}`;

interface CountCellProps {
  poCount: number;
  customerCount: number;
  /** 0..1 ความเข้มของสีพื้น */
  heat: number;
  isToday: boolean;
  bold?: boolean;
  /** จำนวนที่ยังไม่จับคู่ร้านในช่อง — บอกเป็นตัวแดงเล็ก ๆ ให้รู้ว่ามีงานค้าง */
  unmatchedCount?: number;
  onClick: () => void;
}

function CountCell({
  poCount,
  customerCount,
  heat,
  isToday,
  bold = false,
  unmatchedCount = 0,
  onClick,
}: CountCellProps) {
  const isEmpty = poCount === 0;

  return (
    <TableCell
      align="center"
      onClick={isEmpty ? undefined : onClick}
      sx={{
        cursor: isEmpty ? "default" : "pointer",
        whiteSpace: "nowrap",
        bgcolor: (theme) =>
          heat > 0
            ? alpha(theme.palette.primary.main, heat * MAX_HEAT_ALPHA)
            : undefined,
        borderLeft: (theme) =>
          isToday ? `2px solid ${theme.palette.primary.main}` : undefined,
        borderRight: (theme) =>
          isToday ? `2px solid ${theme.palette.primary.main}` : undefined,
        "&:hover": isEmpty
          ? undefined
          : { outline: (theme) => `1px solid ${theme.palette.primary.main}` },
      }}
    >
      {isEmpty ? (
        <Typography variant="body2" color="text.disabled">
          {PO_EMPTY_TEXT}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" fontWeight={bold ? 700 : 600}>
            {numberWithCommas(poCount)} PO · {numberWithCommas(customerCount)}{" "}
            ร้าน
          </Typography>
          {unmatchedCount > 0 && (
            <Typography variant="caption" color="error.main" display="block">
              ยังไม่จับคู่ {numberWithCommas(unmatchedCount)}
            </Typography>
          )}
        </>
      )}
    </TableCell>
  );
}

interface WeeklyRouteGridProps {
  /**
   * HO เท่านั้น: แสดงช่องเลือกสาขา · เลขสายซ้ำกันข้ามสาขา ตารางรวมทุกสาขาจึงไม่มีความหมาย
   * HO ต้องเลือกสาขาก่อน · ผู้ใช้สาขาไม่มีช่องนี้เพราะ backend ปักสาขาจาก token อยู่แล้ว
   */
  selectBranch: boolean;
  onOpenDetail: (uuid: string) => void;
  /** เพิ่มค่าเมื่อผู้เรียกเปลี่ยนข้อมูล (เช่นส่งคืน HO) ให้ตารางอ่านตัวเลขใหม่ */
  refreshToken?: number;
}

/**
 * W7 มุมมอง "ตามสาย × วัน" — หนึ่งสัปดาห์ (จันทร์–เสาร์ + อาทิตย์เมื่อมีของ) ต่อสาย
 * ว่าวันไหนมี PO กี่ใบ กี่ร้าน · แกนวันคือ วันส่ง ของ PO
 *
 * ตัวเลขทุกช่องมาจาก GET /purchase-orders/weekly-summary (ไม่รวมใบที่ยังไม่ระบุสาขา)
 * คลิกช่อง/ยอดรวมเพื่อดูรายการ PO ชุดนั้นจาก API รายการตัวเดิม พร้อม Export Excel
 */
export default function WeeklyRouteGrid({
  selectBranch,
  onOpenDetail,
  refreshToken = 0,
}: WeeklyRouteGridProps) {
  const [weekStart, setWeekStart] = useState<Dayjs>(currentWeekStart);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companyId, setCompanyId] = useState<string>(NO_BRANCH_SELECTED);
  const [data, setData] = useState<PurchaseOrderWeeklySummaryData | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [slice, setSlice] = useState<WeeklySlice | null>(null);

  // คำขอที่ช้ากว่าต้องไม่ทับคำตอบของสัปดาห์ที่ผู้ใช้กดเลื่อนไปแล้ว
  const latestRequestRef = useRef<number>(0);

  const needsBranch = selectBranch && companyId === NO_BRANCH_SELECTED;
  const weekStartText = weekStart.format(PO_API_DATE_FORMAT);

  const loadBranches = useCallback(async () => {
    try {
      const {
        data: { data: rows },
      } = await getAllBranch();
      setBranches(rows ?? []);
    } catch {
      // interceptor แสดง error ให้แล้ว
    }
  }, []);

  useEffect(() => {
    if (selectBranch) loadBranches();
  }, [selectBranch, loadBranches]);

  const loadSummary = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    if (needsBranch) {
      setData(null);
      return;
    }

    setLoading(true);
    try {
      const {
        data: { data: summary },
      } = await getPurchaseOrderWeeklySummary({
        week_start: weekStartText,
        company_id: selectBranch ? companyId : undefined,
      });
      if (latestRequestRef.current !== requestId) return;
      setData(summary ?? null);
    } catch {
      // interceptor แสดง error ให้แล้ว
      if (latestRequestRef.current === requestId) setData(null);
    } finally {
      if (latestRequestRef.current === requestId) setLoading(false);
    }
  }, [needsBranch, weekStartText, selectBranch, companyId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, refreshToken]);

  const routes = useMemo(
    () => (data ? orderWeeklyRoutes(data.routes) : []),
    [data],
  );
  const dayIndexes = useMemo(
    () => (data ? visibleDayIndexes(data) : DEFAULT_DAY_INDEXES),
    [data],
  );
  const maxCount = useMemo(() => maxCellPoCount(routes), [routes]);

  // วันของแต่ละคอลัมน์มาจาก `days` ของ API ถ้ามี · ก่อนคำตอบแรกคำนวณจากวันจันทร์เอง
  const days = useMemo(
    () =>
      data?.days ??
      Array.from({ length: WEEK_LENGTH }, (_, index) =>
        weekStart.add(index, "day").format(PO_API_DATE_FORMAT),
      ),
    [data, weekStart],
  );
  const today = dayjs().format(PO_API_DATE_FORMAT);
  const weekEndText = days[WEEK_LENGTH - 1];
  const columnCount = dayIndexes.length + 2;

  const handlePrevWeek = () => {
    setWeekStart((current) => current.subtract(WEEK_LENGTH, "day"));
  };

  const handleNextWeek = () => {
    setWeekStart((current) => current.add(WEEK_LENGTH, "day"));
  };

  const handleThisWeek = () => {
    setWeekStart(currentWeekStart());
  };

  const handlePickDate = (value: Dayjs | null) => {
    if (value && value.isValid()) setWeekStart(snapToWeekStart(value));
  };

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    setCompanyId(event.target.value);
  };

  const openSlice = (
    title: string,
    from: string,
    to: string,
    routeNumber: string | null | undefined,
  ) => {
    const filter: PurchaseOrderListFilter = {
      delivery_from: from,
      delivery_to: to,
      company_id: selectBranch ? companyId : undefined,
      // undefined = ทุกสาย (ยอดรวมของวัน) · null = ใบที่ยังไม่ผูกร้าน
      route_number:
        routeNumber === undefined
          ? undefined
          : routeNumber ?? PURCHASE_ORDER_NO_ROUTE_FILTER,
    };
    setSlice({ title, filter });
  };

  const handleCloseSlice = () => {
    setSlice(null);
  };

  const weekRangeText = `${formatThaiShortDate(days[0])} – ${formatThaiShortDate(weekEndText)}`;

  return (
    <Box>
      <Paper sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={1.5}
        >
          {selectBranch && (
            <TextField
              select
              size="small"
              label="สาขา"
              value={companyId}
              onChange={handleChangeBranch}
              sx={{ minWidth: 240 }}
              slotProps={{ select: { displayEmpty: true } }}
            >
              <MenuItem value={NO_BRANCH_SELECTED}>เลือกสาขา</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.uuid} value={branch.company_id}>
                  {branch.branch_code} - {branch.branch_name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction="row" alignItems="center" gap={0.5}>
            <Tooltip title="สัปดาห์ก่อน">
              <IconButton size="small" onClick={handlePrevWeek}>
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
              <DatePicker
                label="สัปดาห์ของวันที่"
                value={weekStart}
                onChange={handlePickDate}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: { width: 190 } },
                }}
              />
            </LocalizationProvider>
            <Tooltip title="สัปดาห์ถัดไป">
              <IconButton size="small" onClick={handleNextWeek}>
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
            <Button size="small" onClick={handleThisWeek}>
              สัปดาห์นี้
            </Button>
          </Stack>

          <Typography variant="subtitle1" fontWeight={700}>
            {weekRangeText}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          นับตามวันส่งของ PO · ไม่รวมใบที่ยังไม่ระบุสาขา · คลิกช่องเพื่อดูรายการ PO
        </Typography>
      </Paper>

      <Paper sx={{ p: 0, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead
              sx={{ bgcolor: (theme) => theme.palette.background.default }}
            >
              <TableRow>
                <TableCell>สาย</TableCell>
                {dayIndexes.map((index) => {
                  const isToday = days[index] === today;
                  return (
                    <TableCell
                      key={days[index]}
                      align="center"
                      sx={{
                        whiteSpace: "nowrap",
                        color: isToday ? "primary.main" : undefined,
                        fontWeight: isToday ? 700 : undefined,
                        borderTop: (theme) =>
                          isToday
                            ? `2px solid ${theme.palette.primary.main}`
                            : undefined,
                      }}
                    >
                      {WEEKDAY_SHORT_LABELS[index]}{" "}
                      {formatThaiShortDate(days[index], { weekday: false })}
                      {isToday ? " · วันนี้" : ""}
                    </TableCell>
                  );
                })}
                <TableCell align="center">รวม</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <PurchaseOrderSkeleton
                  columns={columnCount}
                  rows={SKELETON_ROWS}
                  rowHeight={44}
                />
              )}

              {!loading && needsBranch && (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary" fontWeight={500}>
                      เลือกสาขาเพื่อดูตารางสาย × วัน
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading && !needsBranch && routes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่มี PO ส่งในสัปดาห์นี้
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                routes.map((route) => {
                  const label = routeLabel(route.route_number);
                  return (
                    <TableRow key={route.route_number ?? "__no_route__"}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            route.route_number === null
                              ? "error.main"
                              : undefined
                          }
                        >
                          {label}
                        </Typography>
                      </TableCell>
                      {dayIndexes.map((index) => {
                        const cell = route.cells[index];
                        const date = days[index];
                        const poCount = cell?.po_count ?? 0;
                        return (
                          <CountCell
                            key={date}
                            poCount={poCount}
                            customerCount={cell?.customer_count ?? 0}
                            heat={heatRatio(poCount, maxCount)}
                            isToday={date === today}
                            unmatchedCount={
                              route.route_number === null
                                ? 0
                                : cell?.status_counts?.UNMATCHED_CUSTOMER ?? 0
                            }
                            onClick={() =>
                              openSlice(
                                `${label} · ${formatThaiShortDate(date)}`,
                                date,
                                date,
                                route.route_number,
                              )
                            }
                          />
                        );
                      })}
                      <CountCell
                        poCount={route.po_count}
                        customerCount={route.customer_count}
                        heat={0}
                        isToday={false}
                        bold
                        onClick={() =>
                          openSlice(
                            `${label} · ทั้งสัปดาห์ ${weekRangeText}`,
                            days[0],
                            weekEndText,
                            route.route_number,
                          )
                        }
                      />
                    </TableRow>
                  );
                })}

              {!loading && data && routes.length > 0 && (
                <TableRow
                  sx={{ bgcolor: (theme) => theme.palette.background.default }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      รวม
                    </Typography>
                  </TableCell>
                  {dayIndexes.map((index) => {
                    const cell = data.totals.cells[index];
                    const date = days[index];
                    return (
                      <CountCell
                        key={date}
                        poCount={cell?.po_count ?? 0}
                        customerCount={cell?.customer_count ?? 0}
                        heat={0}
                        isToday={date === today}
                        bold
                        onClick={() =>
                          openSlice(
                            `ทุกสาย · ${formatThaiShortDate(date)}`,
                            date,
                            date,
                            undefined,
                          )
                        }
                      />
                    );
                  })}
                  <CountCell
                    poCount={data.totals.po_count}
                    customerCount={data.totals.customer_count}
                    heat={0}
                    isToday={false}
                    bold
                    onClick={() =>
                      openSlice(
                        `ทุกสาย · ทั้งสัปดาห์ ${weekRangeText}`,
                        days[0],
                        weekEndText,
                        undefined,
                      )
                    }
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <WeeklySliceDrawer
        slice={slice}
        onClose={handleCloseSlice}
        onOpenDetail={onOpenDetail}
      />
    </Box>
  );
}
